// NOTE: This file is lazy loading some collections when the user opens the command bar.
//       We are deliberately ignoring the eslint rule here to allow this exception.
import { useLayoutEffect, useRef, useState } from 'react'

import { Box, SearchField } from '@factorialco/deprecated-design-system'
import { AnimatePresence, motion } from 'framer-motion'
import { compact } from 'lodash'

import { observer } from 'mobx-react'

import { fetchCollections } from 'modules/core/components/CollectionsBootstrap'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { track } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import features from 'modules/core/store/collections/features'

import { jobOpeningsPlugin } from 'modules/ats/lib/command_bar_plugins/jobOpeningsPlugin'
import { useCommandBar } from 'modules/command_bar/lib/commandBarContext'
import { documentsPlugins } from 'modules/documents/lib/command_bar_plugin/useDocumentsPlugins'
import Loading from 'modules/layout/components/Page/Loading'
import { settingsPlugin } from 'modules/settings/lib/command_bar_plugins/settings'
import { tasks } from 'modules/tasks/lib/command_bar_plugin/useTasks'

import HelpBar from './components/HelpBar'
import Results from './components/Results'
import * as styles from './index.vanilla.css'
import { devtools } from './plugins/devtools'
import { searchEmployees } from './plugins/employees/useSearchEmployees'
import type { MenuItem } from './plugins/types'
import type { SearchPluginHookData } from './plugins/types'
import { pages } from './plugins/usePages'
import { FADE, SCALE } from './util/animationVariants'

interface Props {
  sidebarLoading: boolean
  menuItems: MenuItem[]
  isOpen: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
}

let trackingSearch = ''

const maxScore = (plugin: SearchPluginHookData) =>
  Math.max(...plugin.results.map((result) => result.score))

const CommandBarSearchResults = ({
  sidebarLoading,
  menuItems,
  search,
  debouncedSearch,
  isLoadingResults,
  setIsLoadingResults,
}: Omit<Props, 'setOpen' | 'isOpen'> & {
  search: string
  debouncedSearch: string
  isLoadingResults: boolean
  setIsLoadingResults: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const plugins = useMemo(
    () =>
      compact([
        tasks,
        searchEmployees,
        pages,
        documentsPlugins,
        jobOpeningsPlugin,
        settingsPlugin,
        devtools,
      ]).flatMap((plugin) => (plugin.filter?.() !== true ? plugin?.hook : [])),
    []
  )

  // TODO: Consolidate this into a single hook
  const pluginSearches = plugins.map((plugin) => plugin(debouncedSearch, sidebarLoading, menuItems))

  const filteredSearches = pluginSearches
    .filter((plugin) => plugin.results.length > 0)
    .map((plugin) => ({
      ...plugin,
      results: plugin.results.filter((result) =>
        result.filter === undefined ? true : result.filter
      ),
    }))
    .sort((a, b) => maxScore(b) - maxScore(a))

  useEffect(() => {
    setIsLoadingResults(search ? !!pluginSearches.find((plugin) => plugin.loading) : false)
  }, [])

  return <Results plugins={filteredSearches} loading={isLoadingResults} search={debouncedSearch} />
}

const CommandBarContent = observer(({ sidebarLoading, menuItems, setOpen, isOpen }: Props) => {
  const [isLoadingResults, setIsLoadingResults] = useState(false)
  const { search, debouncedSearch, clearSearch, onSearch } = useDebouncedSearch('', {
    minimumCharacters: 2,
    delay: true,
  })
  const [collectionsLoaded, setCollectionsLoaded] = useState(false)
  const { setTrakingSearch } = useCommandBar()
  const ref = useRef<HTMLInputElement>(null)

  trackingSearch = search
  setTrakingSearch(search)

  useUnsafeEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code === 'Escape') {
        event.preventDefault()

        track('Create search Cancel', { search_keyword: trackingSearch })
        setOpen(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [setOpen])

  useEffect(() => {
    const initialFetch = async () => {
      if (!collectionsLoaded && isOpen) {
        await fetchCollections([[features, {}]])
        setCollectionsLoaded(true)
      }
    }

    initialFetch()
  }, [collectionsLoaded, isOpen])

  useLayoutEffect(() => {
    if (isOpen && ref.current) {
      ref.current.focus()
    }
    if (!isOpen) {
      clearSearch()
    }
  }, [clearSearch, isOpen])

  if (!isOpen) return null

  return (
    <Box justifyContent="center" className={styles.container}>
      <Box alignItems="center" className={styles.wrapper}>
        <motion.div variants={SCALE} className={styles.box} onClick={(e) => e.stopPropagation()}>
          <>
            <Box>
              <Box paddingY="s16" paddingX="s16" background="grey000White">
                <SearchField
                  ref={ref}
                  variant="big"
                  autofocus
                  onSearch={onSearch}
                  placeholder={i18n.t('command_bar.search')}
                  search={search}
                  clearSearch={clearSearch}
                  loading={isLoadingResults || !collectionsLoaded}
                />
              </Box>
              <Box
                className={styles.content}
                paddingX="s16"
                gap="s4"
                data-testid="commandbar-results"
              >
                {collectionsLoaded ? (
                  <CommandBarSearchResults
                    sidebarLoading={sidebarLoading}
                    menuItems={menuItems}
                    search={search}
                    debouncedSearch={debouncedSearch}
                    isLoadingResults={isLoadingResults}
                    setIsLoadingResults={setIsLoadingResults}
                  />
                ) : (
                  <Loading />
                )}
              </Box>
            </Box>
            <HelpBar />
          </>
        </motion.div>
      </Box>
    </Box>
  )
})

const CommandBar = ({ sidebarLoading, menuItems, isOpen, setOpen }: Props) => {
  return (
    <AnimatePresence>
      <motion.div
        role="search"
        onClick={() => {
          setOpen(false)
          track('Create search Cancel', { search_keyword: trackingSearch })
        }}
        className={styles.background}
        variants={FADE}
        initial="hidden"
        animate="visible"
        exit="exit"
        style={{ display: isOpen ? 'block' : 'none' }}
      >
        <CommandBarContent
          sidebarLoading={sidebarLoading}
          menuItems={menuItems}
          setOpen={setOpen}
          isOpen={isOpen}
        />
      </motion.div>
    </AnimatePresence>
  )
}

export default CommandBar
